import { useCallback } from 'react'
import { useAppsNavigate } from '@wellesley/router'
import {
  SearchResultItemWithType,
  AddRecentSearch,
} from '../../../common/types/search'
import { useGroup } from '@wellesley/group'
import {
  addRecentSearches,
  buildSearchParams,
} from '../../../common/helpers/recentSearchHelpers'

type UseSearchHandlersProps = {
  searchInput: string
  groupName: string | undefined
  foundElsewhere?: boolean
  addRecentSearch: AddRecentSearch
  onClose: () => void
  onResultClick?: (
    item: SearchResultItemWithType,
    query: string,
    groupToSearch?: string,
  ) => void
  onAllResultsClick?: (searchInput: string, groupToSearch?: string) => void
}

export const useSearchHandlers = ({
  searchInput,
  groupName,
  addRecentSearch,
  onClose,
  onResultClick,
  onAllResultsClick,
  foundElsewhere,
}: UseSearchHandlersProps) => {
  const navigate = useAppsNavigate()
  const { group } = useGroup({ groupname: groupName || '' })

  const handleSuggestionClick = useCallback(
    (item: SearchResultItemWithType) => {
      if (item.type === 'tag') {
        addRecentSearch([{ type: 'query', value: `#${item.data}` }])
      } else {
        addRecentSearches({
          searchInput,
          groupName,
          group,
          addRecentSearch,
        })
      }

      onResultClick?.(item, searchInput, groupName)
      onClose()
    },
    [searchInput, groupName, group, addRecentSearch, onResultClick, onClose],
  )

  const handleAllResultsClick = useCallback(() => {
    if (!searchInput.trim()) return

    addRecentSearches({
      searchInput,
      groupName,
      group,
      addRecentSearch,
    })

    if (onAllResultsClick) {
      onAllResultsClick(searchInput, groupName)
    } else {
      navigate('/search', {
        queryParams: buildSearchParams({
          query: searchInput,
          groupName: foundElsewhere ? undefined : groupName,
        }),
      })
    }

    onClose()
  }, [
    foundElsewhere,
    searchInput,
    groupName,
    group,
    addRecentSearch,
    onAllResultsClick,
    navigate,
    onClose,
  ])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && searchInput.trim()) {
        handleAllResultsClick()
      } else if (e.key === 'Escape') {
        onClose()
      }
    },
    [searchInput, handleAllResultsClick, onClose],
  )

  return {
    handleSuggestionClick,
    handleAllResultsClick,
    handleKeyDown,
  }
}
